import type { SimilarMatch } from "../types";

// Re-export for backwards compatibility
export type { SimilarMatch };

interface Props {
  matches: SimilarMatch[];
  isLoading?: boolean;
}

export function SimilarityResults({ matches, isLoading }: Props) {
  // Don't render if no matches
  if (!isLoading && matches.length === 0) {
    return null;
  }

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 90) return { bg: "#10b981", label: "Very Similar" };
    if (similarity >= 75) return { bg: "#f59e0b", label: "Similar" };
    return { bg: "#ef4444", label: "Possibly Related" };
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateHash = (hash: string) => {
    return hash.substring(0, 16) + "...";
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Finding Similar Images...</h3>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Similar Signed Images ({matches.length})</h3>
      <p style={styles.subtitle}>
        Found {matches.length} similar {matches.length === 1 ? "image" : "images"} with 60%+ similarity
      </p>

      <div style={styles.grid}>
        {matches.map((match) => {
          const { bg, label } = getSimilarityColor(match.similarityPercentage);

          return (
            <div key={match.dinoHash} style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={{ ...styles.badge, backgroundColor: bg }}>
                  {match.similarityPercentage}% - {label}
                </span>
              </div>

              <div style={styles.cardBody}>
                <div style={styles.infoRow}>
                  <span style={styles.label}>Signed By:</span>
                  <span style={styles.value}>{match.email}</span>
                </div>

                <div style={styles.infoRow}>
                  <span style={styles.label}>Signed At:</span>
                  <span style={styles.value}>{formatDate(match.timestamp)}</span>
                </div>

                <div style={styles.infoRow}>
                  <span style={styles.label}>Hash:</span>
                  <code style={styles.hashValue}>{truncateHash(match.dinoHash)}</code>
                </div>

                <a
                  href={`https://sepolia.etherscan.io/tx/${match.transactionHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  View on Etherscan →
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: "2rem",
    padding: "1.5rem",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "12px",
    color: "white",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: "600",
    marginBottom: "0.5rem",
  },
  subtitle: {
    fontSize: "0.9rem",
    opacity: 0.9,
    marginBottom: "1.5rem",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "1rem",
  },
  card: {
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    borderRadius: "8px",
    padding: "1rem",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  cardHeader: {
    marginBottom: "1rem",
  },
  badge: {
    display: "inline-block",
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    fontSize: "0.75rem",
    fontWeight: "600",
    color: "white",
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  infoRow: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  label: {
    fontSize: "0.75rem",
    opacity: 0.8,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  value: {
    fontSize: "0.9rem",
    fontWeight: "500",
  },
  hashValue: {
    fontSize: "0.8rem",
    fontFamily: "monospace",
    background: "rgba(0, 0, 0, 0.2)",
    padding: "0.25rem 0.5rem",
    borderRadius: "4px",
  },
  link: {
    color: "white",
    textDecoration: "none",
    fontSize: "0.85rem",
    fontWeight: "500",
    marginTop: "0.5rem",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    transition: "opacity 0.2s",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid rgba(255, 255, 255, 0.3)",
    borderTop: "4px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "2rem auto",
  },
};
